/** Limiter — dynamics processing using the Web Audio DynamicsCompressorNode.
 *
 * Provides two modes:
 * - **transparent**: Gentle limiting with moderate ratio — keeps dynamics natural.
 * - **clipper**: Hard limiting with maximum ratio — acts as a brick-wall limiter.
 *
 * Audio chain:
 *   input → inputGain → compressor → outputGain → output
 */

import type { LimiterParams } from "../types/synth.ts";
import { DEFAULT_LIMITER } from "../types/synth.ts";

/** Clamp a finite number, falling back to a default if not finite. */
function clampFinite(
	value: number,
	min: number,
	max: number,
	fallback: number,
): number {
	if (!Number.isFinite(value)) return fallback;
	return Math.min(Math.max(value, min), max);
}

/** The limiter/compressor effect processor. */
export class Limiter {
	private readonly context: AudioContext;
	private readonly inputNode: GainNode;
	private readonly outputNode: GainNode;
	private readonly inputGain: GainNode;
	private readonly outputGain: GainNode;
	private readonly compressor: DynamicsCompressorNode;
	private params: LimiterParams;

	constructor(context: AudioContext) {
		this.context = context;
		this.params = { ...DEFAULT_LIMITER };

		// Create nodes
		this.inputNode = context.createGain();
		this.outputNode = context.createGain();
		this.inputGain = context.createGain();
		this.outputGain = context.createGain();
		this.compressor = context.createDynamicsCompressor();

		// Wire: inputNode → inputGain → compressor → outputGain → outputNode
		this.inputNode.connect(this.inputGain);
		this.inputGain.connect(this.compressor);
		this.compressor.connect(this.outputGain);
		this.outputGain.connect(this.outputNode);

		// Apply initial params
		this.applyParams();
	}

	/** The node to connect audio sources into. */
	get input(): GainNode {
		return this.inputNode;
	}

	/** The node to connect to downstream destinations. */
	get output(): GainNode {
		return this.outputNode;
	}

	/** The underlying DynamicsCompressorNode (for metering). */
	get compressorNode(): DynamicsCompressorNode {
		return this.compressor;
	}

	/** Current gain reduction in dB (always negative or zero). */
	get reduction(): number {
		return this.compressor.reduction;
	}

	/** Apply all current params to the audio nodes. */
	private applyParams(): void {
		const now = this.context.currentTime;
		const p = this.params;

		if (!p.enabled) {
			// When disabled, bypass: set input gain to 1, output gain to 1, compressor to transparent
			this.inputGain.gain.setValueAtTime(1, now);
			this.outputGain.gain.setValueAtTime(1, now);
			this.compressor.threshold.setValueAtTime(0, now);
			this.compressor.knee.setValueAtTime(40, now);
			this.compressor.ratio.setValueAtTime(1, now);
			this.compressor.attack.setValueAtTime(0.003, now);
			this.compressor.release.setValueAtTime(0.25, now);
			return;
		}

		// Input gain (dB → linear)
		const inputGainLinear = 10 ** (clampFinite(p.inputGain, -24, 24, 0) / 20);
		this.inputGain.gain.setValueAtTime(inputGainLinear, now);

		// Output gain (dB → linear)
		const outputGainLinear = 10 ** (clampFinite(p.outputGain, -24, 24, 0) / 20);
		this.outputGain.gain.setValueAtTime(outputGainLinear, now);

		// Mode-dependent compressor settings
		if (p.mode === "clipper") {
			// Hard brick-wall limiter
			this.compressor.threshold.setValueAtTime(
				clampFinite(p.threshold, -60, 0, -6),
				now,
			);
			this.compressor.knee.setValueAtTime(0, now);
			this.compressor.ratio.setValueAtTime(20, now);
			this.compressor.attack.setValueAtTime(0.001, now);
			this.compressor.release.setValueAtTime(0.01, now);
		} else {
			// Transparent mode — gentle limiting
			this.compressor.threshold.setValueAtTime(
				clampFinite(p.threshold, -60, 0, -12),
				now,
			);
			this.compressor.knee.setValueAtTime(12, now);
			this.compressor.ratio.setValueAtTime(4, now);
			this.compressor.attack.setValueAtTime(0.006, now);
			this.compressor.release.setValueAtTime(0.15, now);
		}
	}

	/** Update limiter parameters. */
	setParams(update: Partial<LimiterParams>): void {
		this.params = { ...this.params, ...update };
		this.applyParams();
	}

	/** Whether the limiter effect is currently enabled. */
	get enabled(): boolean {
		return this.params.enabled;
	}

	/** Current limiter parameters. */
	get currentParams(): Readonly<LimiterParams> {
		return this.params;
	}

	/** Clean up all audio nodes. */
	destroy(): void {
		try {
			this.inputNode.disconnect();
			this.outputNode.disconnect();
			this.inputGain.disconnect();
			this.outputGain.disconnect();
			this.compressor.disconnect();
		} catch {
			// Already disconnected
		}
	}
}
